import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { TAX_RATE } from '../lib/utils';

const B2bContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Instancia de axios independiente para no interferir con el token de staff (PIN)
export const b2bApi = axios.create({ baseURL: API });

export const B2bProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem('b2b_token'));
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem('b2b_cart');
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });

  useEffect(() => {
    if (token) {
      b2bApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      b2bApi.get('/b2b/me')
        .then(r => setCustomer(r.data))
        .catch(() => doLogout())
        .finally(() => setLoading(false));
    } else {
      delete b2bApi.defaults.headers.common['Authorization'];
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    localStorage.setItem('b2b_cart', JSON.stringify(items));
  }, [items]);

  const login = async (username, password) => {
    const r = await b2bApi.post('/b2b/login', { username, password });
    const { access_token, customer: c } = r.data;
    localStorage.setItem('b2b_token', access_token);
    b2bApi.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setCustomer(c);
    return c;
  };

  const doLogout = () => {
    localStorage.removeItem('b2b_token');
    delete b2bApi.defaults.headers.common['Authorization'];
    setToken(null);
    setCustomer(null);
  };

  const addItem = (product, quantity = 1) => {
    setItems(current => {
      const existing = current.find(i => i.product_id === product.id);
      const price = product.reseller_price;
      if (existing) {
        return current.map(i =>
          i.product_id === product.id
            ? { ...i, quantity: i.quantity + quantity, total: (i.quantity + quantity) * i.unit_price }
            : i);
      }
      return [...current, {
        product_id: product.id,
        product_name: product.name,
        sku: product.sku || '',
        quantity,
        unit_price: price,
        total: quantity * price,
        image_url: product.image_url,
        stock: product.stock,
      }];
    });
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) { removeItem(productId); return; }
    setItems(current => current.map(i =>
      i.product_id === productId ? { ...i, quantity, total: quantity * i.unit_price } : i));
  };

  const removeItem = (productId) => setItems(current => current.filter(i => i.product_id !== productId));
  const clearCart = () => setItems([]);

  const subtotal = Math.round(items.reduce((s, i) => s + i.total, 0) * 100) / 100;
  const tax = Math.round(subtotal * TAX_RATE * 100) / 100;
  const total = Math.round((subtotal + tax) * 100) / 100;
  const itemCount = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <B2bContext.Provider value={{
      token, customer, loading, login, logout: doLogout,
      items, addItem, updateQuantity, removeItem, clearCart,
      subtotal, tax, total, itemCount,
    }}>
      {children}
    </B2bContext.Provider>
  );
};

export const useB2b = () => {
  const ctx = useContext(B2bContext);
  if (!ctx) throw new Error('useB2b must be used within a B2bProvider');
  return ctx;
};
